import React from "react";
import { useTranslation } from "react-i18next";
import { MenuItem, Typography } from "@mui/material";
import FunctionsIcon from '@mui/icons-material/Functions';
import { NestedMenu } from "../components";
import {
  BlurVehiclesPeople,
  ClassifyExport,
  ConvertUnits,
  LasToOrthomosaic,
  LasToSolv3dStreamable,
  Sampling,
  ThinLas
} from "./definitions";
import { Asset, AssetType } from "../redux/assets-slice";
import { nanoid } from "@reduxjs/toolkit";
import { useDialog } from "../hooks";

const generateFunctionData = (title, condition, onClick) => {
  return {
    name: title,
    condition: condition,
    component: (
      <MenuItem key={nanoid()} onClick={onClick}>
        <Typography variant="inherit">{title}</Typography>
      </MenuItem>
    )
  };
};

interface FunctionsMenuProps {
  assets: Asset[];
  contextOpen: boolean;
  closeContextMenu: () => void;
}

export const FunctionsMenu = React.forwardRef((props: FunctionsMenuProps, ref) => {
  const {assets, contextOpen, closeContextMenu} = props;

  const {t} = useTranslation();
  const blurVehiclesPeopleDialog = useDialog();
  const lasToOrthomosaicDialog = useDialog();
  const lasToSolv3dStreamableDialog = useDialog();
  const thinLasDialog = useDialog();
  const samplingDialog = useDialog();
  const classifyExportDialog = useDialog();
  const convertUnitsDialog = useDialog();

  const LASFiles = assets
    .filter(x => x.type === AssetType.LAS);

  const cameraFiles = assets
    .filter(x => x.type === AssetType.Panoramic);

  const hasLASFiles = LASFiles.length > 0;
  const hasCameraFiles = cameraFiles.length > 0;

  const handleBlurVehiclesPeople = () => {
    closeContextMenu();
    blurVehiclesPeopleDialog.handleOpen();
  };

  const handleLasToOrthomosaic = () => {
    closeContextMenu();
    lasToOrthomosaicDialog.handleOpen();
  };

  const handleLasToSolv3dStreamable = () => {
    closeContextMenu();
    lasToSolv3dStreamableDialog.handleOpen();
  };

  const handleThinLas = () => {
    closeContextMenu();
    thinLasDialog.handleOpen();
  };

  const handleSampling = () => {
    closeContextMenu();
    samplingDialog.handleOpen();
  };

  const handleClassifyExport = () => {
    closeContextMenu();
    classifyExportDialog.handleOpen();
  };

  const handleConvertUnits = () => {
    closeContextMenu();
    convertUnitsDialog.handleOpen();
  };

  const getMenuItems = () => {
    if (!contextOpen) return [];

    const data = [
      generateFunctionData(
        t('functions.blur_vehicles_people.title'),
        hasCameraFiles,
        handleBlurVehiclesPeople
      ),
      generateFunctionData(
        t('functions.las_to_orthomosaic.title'),
        hasLASFiles,
        handleLasToOrthomosaic
      ),
      generateFunctionData(
        t('functions.las_to_solv3d_streamable.title'),
        hasLASFiles,
        handleLasToSolv3dStreamable
      ),
      generateFunctionData(
        t('functions.thin_las.title'),
        hasLASFiles,
        handleThinLas
      ),
      generateFunctionData(
        t('functions.sampling.title'),
        hasLASFiles,
        handleSampling
      ),
      generateFunctionData(
        t('functions.classify_export.title'),
        hasLASFiles,
        handleClassifyExport
      ),
      generateFunctionData(
        t('functions.convert_units.title'),
        hasLASFiles,
        handleConvertUnits
      )
    ];

    let filtered = data
      .filter(x => x.condition)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(x => x.component);

    if (filtered.length === 0) {
      return (
        <MenuItem disabled>
          <Typography variant="inherit">
            {t('functions.no_functions_available')}
          </Typography>
        </MenuItem>
      );
    }

    return filtered;
  };

  return (
    <React.Fragment>
      <NestedMenu
        ref={ref}
        text={t('functions.execute_function')}
        icon={<FunctionsIcon fontSize="small"/>}
        parentMenuOpen={contextOpen}
      >
        {getMenuItems()}
      </NestedMenu>

      {hasCameraFiles && <React.Fragment>

        {/** Blur vehicles and people */}
        <BlurVehiclesPeople
          assets={cameraFiles}
          open={blurVehiclesPeopleDialog.open}
          onClose={blurVehiclesPeopleDialog.handleClose}
        />

      </React.Fragment>}

      {hasLASFiles && <React.Fragment>

        {/** LAS to Orthomosaic */}
        <LasToOrthomosaic
          assets={LASFiles}
          open={lasToOrthomosaicDialog.open}
          onClose={lasToOrthomosaicDialog.handleClose}
        />

        {/** LAS to Solv3D Streamable Point Cloud */}
        <LasToSolv3dStreamable
          assets={LASFiles}
          open={lasToSolv3dStreamableDialog.open}
          onClose={lasToSolv3dStreamableDialog.handleClose}
        />

        {/** Thin Las */}
        <ThinLas
          assets={LASFiles}
          open={thinLasDialog.open}
          onClose={thinLasDialog.handleClose}
        />

        {/** Sampling */}
        <Sampling
          assets={LASFiles}
          open={samplingDialog.open}
          onClose={samplingDialog.handleClose}
        />

        {/** Sampling */}
        <ClassifyExport
          assets={LASFiles}
          open={classifyExportDialog.open}
          onClose={classifyExportDialog.handleClose}
        />

        {/** Convert Units */}
        <ConvertUnits
          assets={LASFiles}
          open={convertUnitsDialog.open}
          onClose={convertUnitsDialog.handleClose}
        />

      </React.Fragment>}

    </React.Fragment>
  );
});
